import { useState, useCallback, useEffect } from "react";
import { Loader2, MapPin, CheckCircle2, AlertTriangle, Navigation } from "lucide-react";
import { fetchCep, formatCep } from "@/lib/cep-utils";
import { isWithinDeliveryRadius } from "@/lib/geo-utils";
import DeliveryMap from "./DeliveryMap";

interface DeliveryAddressFormProps {
  restaurant: any;
  primaryColor: string;
  onValidated: (address: string, distance: number | null, fee: number | null, within: boolean) => void;
  onReset: () => void;
  validation: "idle" | "checking" | "ok" | "too_far" | "not_found";
  setValidation: (v: "idle" | "checking" | "ok" | "too_far" | "not_found") => void;
  distance: number | null;
}

const inputClass = "w-full h-11 rounded-xl bg-[hsl(0,0%,96%)] px-4 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20";

const STORAGE_KEY = "viamenu_last_delivery_address";

interface SavedAddress {
  cep: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  number: string;
  noNumber: boolean;
  reference: string;
}

function saveAddress(data: SavedAddress) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

function loadAddress(): SavedAddress | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

async function reverseGeocode(lat: number, lng: number): Promise<{
  street?: string; number?: string; neighborhood?: string;
  city?: string; state?: string; postcode?: string;
} | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=pt-BR`,
      { headers: { "User-Agent": "ViaMenu/1.0" } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    const addr = data.address || {};
    return {
      street: addr.road || addr.pedestrian || addr.footway || "",
      number: addr.house_number || "",
      neighborhood: addr.suburb || addr.neighbourhood || addr.quarter || "",
      city: addr.city || addr.town || addr.village || addr.municipality || "",
      state: addr.state || "",
      postcode: (addr.postcode || "").replace(/\D/g, ""),
    };
  } catch { return null; }
}

const DeliveryAddressForm = ({ restaurant, primaryColor, onValidated, onReset, validation, setValidation, distance }: DeliveryAddressFormProps) => {
  const [cep, setCep] = useState("");
  const [street, setStreet] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [number, setNumber] = useState("");
  const [noNumber, setNoNumber] = useState(false);
  const [reference, setReference] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  const [cepFound, setCepFound] = useState(false);
  const [customerCoords, setCustomerCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [restoredFromStorage, setRestoredFromStorage] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoStatus, setGeoStatus] = useState<"idle" | "denied" | "error" | "found">("idle");

  // Restore last address on mount
  useEffect(() => {
    const saved = loadAddress();
    if (saved && saved.cep && saved.street) {
      setCep(saved.cep);
      setStreet(saved.street);
      setNeighborhood(saved.neighborhood || "");
      setCity(saved.city || "");
      setState(saved.state || "");
      setNumber(saved.number || "");
      setNoNumber(saved.noNumber || false);
      setReference(saved.reference || "");
      setCepFound(true);
      setRestoredFromStorage(true);
    }
  }, []);

  const handleUseLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setGeoStatus("error");
      return;
    }
    setGeoLoading(true);
    setGeoStatus("idle");
    onReset();
    setCustomerCoords(null);
    setRestoredFromStorage(false);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCustomerCoords({ lat: latitude, lng: longitude });
        const addr = await reverseGeocode(latitude, longitude);
        setGeoLoading(false);

        if (addr && (addr.street || addr.postcode)) {
          setStreet(addr.street || "");
          setNeighborhood(addr.neighborhood || "");
          setCity(addr.city || "");
          setState(addr.state || "");
          if (addr.number) {
            setNumber(addr.number);
            setNoNumber(false);
          } else {
            setNumber("");
          }
          if (addr.postcode && addr.postcode.length === 8) {
            setCep(formatCep(addr.postcode));
            setCepFound(true);
          } else {
            setCep("");
            setCepFound(true); // still show fields even without CEP
          }
          setGeoStatus("found");
        } else {
          setGeoStatus("error");
        }
      },
      (err) => {
        setGeoLoading(false);
        setGeoStatus(err.code === 1 ? "denied" : "error");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [onReset]);

  const handleCepChange = useCallback(async (raw: string) => {
    const formatted = formatCep(raw);
    setCep(formatted);
    onReset();
    setCustomerCoords(null);

    const digits = raw.replace(/\D/g, "");
    if (digits.length === 8) {
      setCepLoading(true);
      const data = await fetchCep(digits);
      setCepLoading(false);
      if (data) {
        setStreet(data.logradouro || "");
        setNeighborhood(data.bairro || "");
        setCity(data.localidade || "");
        setState(data.uf || "");
        setCepFound(true);
      } else {
        setCepFound(false);
      }
    } else {
      setCepFound(false);
    }
  }, [onReset]);

  const buildFullAddress = useCallback(() => {
    const parts = [street];
    if (!noNumber && number) parts[0] += `, ${number}`;
    if (noNumber) parts[0] += ", S/N";
    if (neighborhood) parts.push(neighborhood);
    if (city) parts.push(city);
    if (state) parts.push(state);
    if (reference) parts.push(`Ref: ${reference}`);
    return parts.join(", ");
  }, [street, number, noNumber, neighborhood, city, state, reference]);

  const canValidate = cepFound && street && (number || noNumber);

  const handleValidate = useCallback(async () => {
    const rest = restaurant;
    const fullAddress = buildFullAddress();

    if (!rest?.address_lat || !rest?.address_lng || !rest?.delivery_radius_km) {
      onValidated(fullAddress, null, null, true);
      setValidation("ok");
      return;
    }

    setValidation("checking");
    const streetWithNum = noNumber ? street : `${street}, ${number}`;
    const result = await isWithinDeliveryRadius(
      { lat: rest.address_lat, lng: rest.address_lng },
      fullAddress,
      rest.delivery_radius_km
    );

    if (result.distance === null) {
      // Try structured search
      const { geocodeAddress, haversineDistanceKm } = await import("@/lib/geo-utils");
      const coords = await geocodeAddress(fullAddress, {
        street: streetWithNum,
        city,
        state,
        postalcode: cep,
        country: "Brazil",
      });
      if (coords) {
        const dist = haversineDistanceKm({ lat: rest.address_lat, lng: rest.address_lng }, coords);
        const roundDist = Math.round(dist * 10) / 10;
        const within = dist <= rest.delivery_radius_km;
        setCustomerCoords(coords);
        const feePerKm = Number(rest.delivery_fee_per_km || 0);
        const freeUntil = Number(rest.delivery_free_until_km || 0);
        let fee: number | null = null;
        if (feePerKm > 0) {
          fee = Math.round(Math.max(0, dist - freeUntil) * feePerKm * 100) / 100;
        } else {
          fee = Number(rest.delivery_fee || 0);
        }
        onValidated(fullAddress, roundDist, fee, within);
        setValidation(within ? "ok" : "too_far");
        if (within) saveAddress({ cep, street, neighborhood, city, state, number, noNumber, reference });
      } else {
        onValidated(fullAddress, null, null, false);
        setValidation("not_found");
      }
    } else {
      setCustomerCoords(result.customerCoords);
      const feePerKm = Number(rest.delivery_fee_per_km || 0);
      const freeUntil = Number(rest.delivery_free_until_km || 0);
      let fee: number | null = null;
      if (feePerKm > 0) {
        fee = Math.round(Math.max(0, result.distance - freeUntil) * feePerKm * 100) / 100;
      } else {
        fee = Number(rest.delivery_fee || 0);
      }
      onValidated(fullAddress, result.distance, fee, result.within);
      setValidation(result.within ? "ok" : "too_far");
      if (result.within) saveAddress({ cep, street, neighborhood, city, state, number, noNumber, reference });
    }
  }, [restaurant, buildFullAddress, noNumber, street, number, city, state, cep, neighborhood, reference, onValidated, setValidation]);

  const hasRestaurantCoords = restaurant?.address_lat && restaurant?.address_lng && restaurant?.delivery_radius_km;

  return (
    <div className="space-y-3">
      {/* Geolocation button */}
      {!cepFound && geoStatus !== "found" && (
        <button
          onClick={handleUseLocation}
          disabled={geoLoading}
          className="w-full h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors border-2 border-dashed"
          style={{
            borderColor: `${primaryColor}40`,
            backgroundColor: `${primaryColor}08`,
            color: primaryColor,
          }}
        >
          {geoLoading ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Detectando localização...</>
          ) : (
            <><Navigation className="h-4 w-4" /> Usar minha localização</>
          )}
        </button>
      )}

      {geoStatus === "denied" && !cepFound && (
        <div className="flex items-center gap-2 text-xs text-[hsl(35,100%,40%)] px-1 font-medium">
          <AlertTriangle className="h-3.5 w-3.5" /> Localização negada. Preencha o CEP manualmente.
        </div>
      )}
      {geoStatus === "error" && !cepFound && (
        <div className="flex items-center gap-2 text-xs text-[hsl(35,100%,40%)] px-1 font-medium">
          <AlertTriangle className="h-3.5 w-3.5" /> Não foi possível detectar. Preencha o CEP.
        </div>
      )}

      {geoStatus === "found" && !cepFound && (
        <div className="flex items-center gap-2 text-xs text-[hsl(142,71%,40%)] px-1 font-medium">
          <CheckCircle2 className="h-3.5 w-3.5" /> Endereço detectado pela localização. Confira abaixo.
        </div>
      )}

      {/* CEP */}
      <div className="relative">
        <input
          className={inputClass}
          placeholder="CEP *"
          value={cep}
          onChange={(e) => handleCepChange(e.target.value)}
          maxLength={9}
          inputMode="numeric"
        />
        {cepLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[hsl(0,0%,50%)]" />
        )}
        {cepFound && !cepLoading && (
          <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(142,71%,40%)]" />
        )}
      </div>

      {cepFound && (
        <>
          {restoredFromStorage && validation === "idle" && (
            <div className="flex items-center gap-2 text-xs text-[hsl(210,60%,50%)] px-1 font-medium">
              <MapPin className="h-3.5 w-3.5" /> Endereço anterior restaurado. Verifique e confirme.
            </div>
          )}
          {/* Street (read-only from CEP) */}
          <input
            className={`${inputClass} bg-[hsl(0,0%,94%)] text-[hsl(0,0%,40%)]`}
            value={street}
            readOnly
            placeholder="Rua"
          />

          {/* Number + No number */}
          <div className="flex gap-2 items-center">
            <input
              className={`${inputClass} flex-1`}
              placeholder="Número *"
              value={number}
              onChange={(e) => { setNumber(e.target.value); onReset(); setCustomerCoords(null); }}
              disabled={noNumber}
              inputMode="numeric"
            />
            <label className="flex items-center gap-1.5 text-xs text-[hsl(0,0%,50%)] whitespace-nowrap cursor-pointer select-none">
              <input
                type="checkbox"
                checked={noNumber}
                onChange={(e) => { setNoNumber(e.target.checked); if (e.target.checked) setNumber(""); onReset(); setCustomerCoords(null); }}
                className="rounded border-[hsl(0,0%,80%)] accent-current"
                style={{ accentColor: primaryColor }}
              />
              Sem nº
            </label>
          </div>

          {/* Neighborhood + City (read-only) */}
          <div className="grid grid-cols-2 gap-2">
            <input className={`${inputClass} bg-[hsl(0,0%,94%)] text-[hsl(0,0%,40%)]`} value={neighborhood} readOnly placeholder="Bairro" />
            <input className={`${inputClass} bg-[hsl(0,0%,94%)] text-[hsl(0,0%,40%)]`} value={`${city}${state ? ` - ${state}` : ""}`} readOnly placeholder="Cidade" />
          </div>

          {/* Reference */}
          <input
            className={inputClass}
            placeholder="Ponto de referência (opcional)"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />

          {/* Validate button */}
          {canValidate && validation === "idle" && (
            <button
              onClick={handleValidate}
              className="w-full h-10 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
              style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
            >
              <MapPin className="h-3.5 w-3.5" /> Verificar se está na área de entrega
            </button>
          )}

          {validation === "checking" && (
            <div className="flex items-center gap-2 text-xs text-[hsl(0,0%,50%)] px-1">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verificando endereço...
            </div>
          )}
          {validation === "ok" && distance !== null && (
            <div className="flex items-center gap-2 text-xs text-[hsl(142,71%,40%)] px-1 font-medium">
              <CheckCircle2 className="h-3.5 w-3.5" /> Endereço dentro da área de entrega ({distance} km)
            </div>
          )}
          {validation === "too_far" && (
            <div className="flex items-center gap-2 text-xs text-[hsl(0,84%,50%)] px-1 font-medium">
              <AlertTriangle className="h-3.5 w-3.5" /> Fora da área de entrega ({distance} km). Raio máximo: {restaurant?.delivery_radius_km || "?"} km
            </div>
          )}
          {validation === "not_found" && (
            <div className="flex items-center gap-2 text-xs text-[hsl(35,100%,40%)] px-1 font-medium">
              <AlertTriangle className="h-3.5 w-3.5" /> Endereço não encontrado. Verifique e tente novamente.
            </div>
          )}

          {/* Leaflet Map */}
          {hasRestaurantCoords && (
            <DeliveryMap
              restaurantLat={restaurant.address_lat}
              restaurantLng={restaurant.address_lng}
              radiusKm={restaurant.delivery_radius_km}
              customerLat={customerCoords?.lat}
              customerLng={customerCoords?.lng}
              primaryColor={primaryColor}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DeliveryAddressForm;
